import React, { useEffect, useRef } from "react";
import { X } from "lucide-react";

export default function QueryDialog({ title, onClose, busy = false, children }) {
  const dialog = useRef(null);
  const close = useRef(onClose);
  const locked = useRef(busy);
  close.current = onClose;
  locked.current = busy;

  useEffect(() => {
    const previousFocus = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.current?.focus();
    const handleKey = (event) => {
      if (event.key === "Escape" && !locked.current) close.current();
      if (event.key !== "Tab") return;
      const elements = dialog.current?.querySelectorAll('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), audio[controls]');
      if (!elements?.length) { event.preventDefault(); return; }
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog.current)) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener("keydown", handleKey);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-3 backdrop-blur-sm sm:p-6" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}>
      <div ref={dialog} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="query-dialog-title" className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl outline-none dark:bg-gray-900">
        <div className="flex items-center justify-between border-b border-orange-100 bg-orange-50/60 px-5 py-4 dark:border-gray-800 dark:bg-gray-800/50">
          <h2 id="query-dialog-title" className="text-lg font-bold text-[#4A1B13] dark:text-orange-100">{title}</h2>
          <button onClick={onClose} disabled={busy} aria-label="Close query details" className="rounded-lg p-2 text-slate-500 hover:bg-orange-100 disabled:opacity-40 dark:hover:bg-gray-700"><X size={20} /></button>
        </div>
        <div className="overflow-y-auto p-5 sm:p-6">{children}</div>
      </div>
    </div>
  );
}
